<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit, { type ITableSortSettings, type ITableColumn, type TTableRow } from '@pocketprep/ui-kit'
import AddGroupSidePanel from '@/components/SidePanels/AddGroup.vue'
import EditGroupSidePanel from '@/components/SidePanels/EditGroup.vue'
import { groupModule } from '@/store/group/module'
import { instructorModule } from '@/store/instructor/module'
import { examModule } from '@/store/exam/module'
import { licenseModule } from '@/store/license/module'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'

const {
    Table,
    Button: PocketButton,
    Tooltip,
    Icon,
    SegmentControl,
    OverflowTooltip,
} = UIKit

interface IMappedGroup extends TTableRow {
    id: string
    name: string
    creator: string
    examGuid: string
    students: number
    prep: string
    access: string
    segmentControlValue: number
    hideRowEdit: boolean | undefined
    styles: {
        [key: string]: string
    }
    tooltips?: {
        examPrep?: string
    }
}

const activeTab = ref<'personal' | 'organization'>('personal')
const showAddGroupSidePanel = ref(false)
const showEditGroupSidePanel = ref(false)
const editGroupId = ref<null | string>(null)
const rowHoverId = ref<string | number | null>(null)
const cellHovered = ref<null | { propName: string; id: string }>(null)

const groupColumns: ITableColumn[] = [{
    name: 'Group Name',
    propName: 'name',
    styles: {
        width: '226px',
    },
}, {
    name: '# Students',
    propName: 'students',
    styles: {
        width: '75px',
        textAlign: 'center',
    },
}, {
    name: 'Prep',
    propName: 'prep',
    styles: {
        width: '212px',
    },
}]
const groupColumnsAppend: ITableColumn[] = [{
    name: 'Stats Tab Use',
    propName: 'showHide',
    styles: {
        margin: '0 0 0 0',
        width: '114px',
        overflow: 'visible',
    },
}, {
    propName: 'edit',
    isLabelHidden: true,
    styles: {
        margin: '0 0 0 0',
        width: '24px',
    },
}]
const sortSettings = ref<ITableSortSettings>({ column: groupColumns[1], direction: 1 })

const yourOrgExamIds = computed(() => {
    return examModule.getters.getYourOrgExamIds()
})

const currentVisibleOrgGroups = computed(() => {
    return groupModule.getters.getCurrentVisibleOrgGroups()
})

const currentOrgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const organizationGroupColumns = computed(() => {
    return [
        ...groupColumns,
        {
            name: 'Creator',
            propName: 'creator',
            styles: {
                width: '76px',
            },
        },
        ...groupColumnsAppend,
    ]
})

const personalGroupColumns = computed(() => {
    return [
        ...groupColumns,
        {
            name: 'Access',
            propName: 'access',
            styles: {
                width: '76px',
            },
        },
        ...groupColumnsAppend,
    ]
})

const compositeGroups = computed(() => {
    return groupModule.getters.getCompositeGroups()
})

/**
 * "My Groups" are all the non-system composite groups that the current user owns
 */
const userGroups = computed(() => {
    return compositeGroups.value.filter(g => g !== undefined
        && g.owner && g.owner.objectId === currentOrgInstructor.value?.objectId && g.type !== 'system'
    )
})

/**
 * "Organization Groups" are all the system and public composite groups owned by non-pending instructors
 * for instructors, they exclude groups for exams that they have not been added to
 */
const organizationGroups = computed(() => {
    return compositeGroups.value.filter(g => {
        return g !== undefined
            && (g.type !== 'private' || currentOrgInstructor.value?.objectId === g.owner?.objectId)
            && (currentOrgInstructor.value?.isAdmin || yourOrgExamIds.value.includes(g.orgExam.objectId))
    })
})

const visibleGroupIds = computed(() => {
    return currentVisibleOrgGroups.value.map(g => g && g.objectId)
})

const mappedGroups = computed(() => {
    const sortSettingsVal = sortSettings.value
    const groupArray = activeTab.value === 'personal' ? userGroups.value : organizationGroups.value
    return groupArray.map((compositeGroup) => {
        if (!compositeGroup) {
            return
        }
        const groups = compositeGroup.groups
        const id = groups[0]?.objectId   // Using first group's id
        const owner = compositeGroup.owner
        const type = compositeGroup.type

        const styles: { [key: string]: string } = {}
        if (rowHoverId.value === id) {
            styles.backgroundColor = `${BrandColors.grayBackground}60`
        }

        return {
            id,
            name: compositeGroup.longName,
            creator: compositeGroup.creator,
            examGuid: compositeGroup.examGuid,
            styles,
            students: compositeGroup.orgStudents.length,
            prep: compositeGroup.examName || '--',
            access: type === 'private' ? 'Private' : type === 'system' ? 'System' : 'Shared',
            segmentControlValue: groups.some(g => visibleGroupIds.value.includes(g.objectId)) ? 2 : 1,
            hideRowEdit: type === 'system' || (
                owner
                && owner.objectId !== currentOrgInstructor.value?.objectId
                && !compositeGroup.isPubliclyEditable
            ),
        } as IMappedGroup
    })
        .filter(g => g !== undefined)
        .sort((a, b) => {
            if (sortSettingsVal?.column) {
                if (sortSettingsVal.column.name === 'Group Name') {
                    return sortSettingsVal.direction * a.name.localeCompare(b.name)
                }
                if (sortSettingsVal.column.name === '# Students') {
                    return sortSettingsVal.direction * (b.students - a.students)
                }
                if (sortSettingsVal.column.name === 'Prep') {
                    return sortSettingsVal.direction * (a.prep).localeCompare(b.prep)
                }
                if (sortSettingsVal.column.name === 'Creator') {
                    return sortSettingsVal.direction * (a.creator).localeCompare(b.creator)
                }
                if (sortSettingsVal.column.name === 'Access') {
                    return sortSettingsVal.direction * (a.access).localeCompare(b.access)
                }
            }

            return -1
        })
})

onMounted(async () => {
    await Promise.all([
        groupModule.actions.fetchCurrentOrgGroups(),
        groupModule.actions.fetchCurrentVisibleOrgGroups(),
        examModule.actions.fetchCurrentOrgExams(),
        instructorModule.actions.fetchCurrentOrgInstructors(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
        groupModule.actions.fetchGroupsActiveStudentIds(),
        examModule.actions.fetchYourOrgExamIds(),
        examModule.actions.fetchExamMetadata(),
    ])
})

const changeTab = (tabName: 'personal' | 'organization') => {
    activeTab.value = tabName
}

const toggleGroup = async ({ row, option }: { row: IMappedGroup; option: number }) => {
    /* visibleOrgGroups are not preserved in loadable state, so it's possible to get into a state where
    we are attempting to update the visiblity of orgGroups while they are already visible/not visible

    Need a check for rowId already existing and remove it if we are in this state. I played around with
    loading states but it results in weird SegmentControl behavior, so this feels like the best choice.
    */
    const orgGroup = currentOrgGroups.value[row.id]

    const groupIdsToUpdate: string[] = []

    if (orgGroup) {
        const toggleGroupCompositeKey = groupModule.getters.getGroupCompositeKeyForGroupId(orgGroup.objectId) as string
        const groupsInCompositeGroup = groupModule.getters.getGroupsByGroupCompositeKey()[toggleGroupCompositeKey]

        if (groupsInCompositeGroup) {
            groupIdsToUpdate.push(...groupsInCompositeGroup.map(g => g.objectId))
        }
    } else {
        groupIdsToUpdate.push(row.id)
    }
    const visibleGroupIdsVal = currentVisibleOrgGroups.value
        .map(g => g.objectId).filter(id => !groupIdsToUpdate.includes(id))
    const groupIds = option === 1
        ? visibleGroupIdsVal
        : [ ...visibleGroupIdsVal, ...groupIdsToUpdate ]

    await instructorModule.actions.updateVisibleOrgGroups(groupIds)
}

const updateSortSettings = (sortSettingsVal: ITableSortSettings) => {
    sortSettings.value = sortSettingsVal
}

const editClicked = (groupId: string) => {
    showEditGroupSidePanel.value = true
    editGroupId.value = groupId
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = row.id
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

const hideCellTooltip = () => {
    cellHovered.value = null
}

const showCellTooltip = (propName: string, id: string) => {
    cellHovered.value = {
        propName,
        id,
    }
}
</script>

<template>
    <div class="groups">
        <AddGroupSidePanel v-if="showAddGroupSidePanel" @close="() => showAddGroupSidePanel = false" />
        <EditGroupSidePanel
            v-if="showEditGroupSidePanel"
            :group-id="editGroupId || ''"
            @close="() => { showEditGroupSidePanel = false; editGroupId = null }"
        />
        <div class="groups__tabs">
            <div
                class="groups__tab"
                :class="{
                    'groups__tab--active': activeTab === 'personal'
                }"
                @click="changeTab('personal')"
            >
                My Groups ({{ userGroups.length }})
            </div>
            <div
                class="groups__tab"
                :class="{
                    'groups__tab--active': activeTab === 'organization'
                }"
                @click="changeTab('organization')"
            >
                Organization Groups ({{ organizationGroups.length }})
            </div>
        </div>
        <Table
            :rows="mappedGroups"
            :columns="activeTab === 'personal' ? personalGroupColumns : organizationGroupColumns"
            :default-sort="sortSettings"
            :sort-toggle-order="[ 1, -1 ]"
            :show-header="false"
            :table-row-styles="{
                paddingRight: '0',
                height: '45px'
            }"
            :table-column-labels-styles="{
                paddingRight: '0'
            }"
            :table-cell-styles="{
                position: 'relative',
            }"
            class="groups__table"
            @sort="updateSortSettings"
            @rowEnter="rowEnter"
            @rowLeave="rowLeave"
        >
            <template #tableCellValue="{ row, column }">
                <template v-if="column.propName === 'edit'">
                    <Tooltip
                        v-if="cellHovered
                            && (cellHovered.propName === column.propName && cellHovered.id === row.id)"
                        class="groups__edit-tooltip"
                    >
                        Edit Group
                    </Tooltip>
                    <Icon
                        v-show="rowHoverId === row.id && !row.hideRowEdit"
                        class="groups__edit"
                        type="edit"
                        @mouseover="showCellTooltip(column.propName, row.id)"
                        @mouseout="hideCellTooltip"
                        @click="editClicked(row.id)"
                    />
                </template>
                <template v-else-if="column.propName === 'examPrep'">
                    <Tooltip
                        v-if="cellHovered
                            && (cellHovered.propName === column.propName && cellHovered.id === row.id)
                            && row.tooltips
                            && typeof row.tooltips.examPrep === 'string'"
                        class="groups__exam-prep-tooltip"
                        theme="rightalign"
                    >
                        <div v-html="row.tooltips.examPrep" />
                    </Tooltip>
                    <div
                        class="groups__exam-prep"
                        @mouseover="showCellTooltip(column.propName, row.id)"
                        @mouseout="hideCellTooltip"
                        v-html="row[column.propName]"
                    />
                </template>
                <template v-else-if="column.propName === 'showHide'">
                    <SegmentControl
                        :defaultOption="row.segmentControlValue"
                        option1="Hide"
                        option2="Show"
                        @change="toggleGroup({ row, option: $event })"
                    />
                </template>
                <div
                    v-else-if="column.propName === 'name' && activeTab === 'personal'"
                    class="groups__name"
                >
                    <OverflowTooltip>
                        {{ row[column.propName] }}
                    </OverflowTooltip>
                </div>
                <div
                    v-else-if="column.propName === 'name' && activeTab === 'organization'"
                    class="groups__name"
                    v-html="row[column.propName]"
                />
                <div v-else v-html="row[column.propName]" />
            </template>
        </Table>
        <PocketButton type="secondary" @click="showAddGroupSidePanel = true">
            + Add New Group
        </PocketButton>
    </div>
</template>

<style lang="scss" scoped>


.groups {
    button {
        padding: 9px 11px 8px;
    }

    &__tabs {
        border-bottom: 2px solid $gray-divider;
        display: flex;
        font-size: 16px;
        line-height: 22px;
        font-weight: 500;
        margin-bottom: 25px;
    }

    &__tab {
        position: relative;
        padding: 6px 8px;
        cursor: pointer;
        user-select: none;
        margin-right: 9px;

        &--active {
            font-weight: 600;

            &::after {
                content: '';
                width: 100%;
                height: 2px;
                bottom: -2px;
                left: 0;
                position: absolute;
                background: $brand-blue;
            }
        }
    }

    &__name:deep(span) {
        font-size: 15px;
        color: $slate-01;
    }

    &__edit {
        cursor: pointer;
        height: 24px;
        color: $brand-blue;
    }

    &__edit-tooltip {
        position: absolute;
        transform: translate(0, -100%);
        left: 12px;
        top: -3px;
    }
}
</style>
